<script setup lang="ts">
/**
 * A parent row that folds its children, for a work list read by theme: a toggle that says whether it is open, the parent's title, how many
 * children it holds and a rollup of their states. The open state belongs to whoever holds the row (`v-model:open`),
 * so a page that re-reads its list keeps a parent open by keying the state on the item, not on this component. A
 * press only asks to change it (`update:open`); the row does not open or close itself.
 *
 * While closed the children do not render at all, so a long campaign costs nothing until a reader opens it.
 *
 * Slots: `title` (inside the toggle, so plain words, no link), `rollup` (the children's states), `aside` (the row's
 * own facts and links, outside the toggle) and the default slot (the children).
 */
import { useId } from 'vue'

const props = withDefaults(
    defineProps<{
        /** How many children the row holds. */
        count: number
        /** Whether the children show. Controlled with `v-model:open`. */
        open?: boolean
    }>(),
    { open: false },
)

const emit = defineEmits<{
    'update:open': [open: boolean]
}>()

const regionId = `zh-group-row-${useId()}`

function toggle(): void {
    emit('update:open', !props.open)
}
</script>

<template>
    <div class="zh-group-row" :data-open="open ? 'true' : undefined">
        <div class="zh-group-row__head">
            <button
                type="button"
                class="zh-group-row__toggle"
                :aria-expanded="open ? 'true' : 'false'"
                :aria-controls="regionId"
                @click="toggle"
            >
                <span class="zh-group-row__marker" aria-hidden="true">{{ open ? '▾' : '▸' }}</span>
                <span class="zh-group-row__title"><slot name="title" /></span>
                <span class="zh-group-row__count">{{ count }}</span>
            </button>
            <span class="zh-group-row__rollup"><slot name="rollup" /></span>
            <span v-if="$slots.aside" class="zh-group-row__aside"><slot name="aside" /></span>
        </div>
        <div v-if="open" :id="regionId" class="zh-group-row__children">
            <slot />
        </div>
    </div>
</template>

<style scoped>
.zh-group-row {
    display: grid;
    gap: 0.375rem;
}

.zh-group-row__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
}

.zh-group-row__toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    padding: 0;
    border: 0;
    background: none;
    color: var(--ink-1);
    font: inherit;
    text-align: left;
    cursor: pointer;
}

.zh-group-row__toggle:hover .zh-group-row__title,
.zh-group-row__toggle:focus-visible .zh-group-row__title {
    text-decoration: underline;
}

/* A keyboard reader sees where they are, in the same ring the theme's buttons use. */
.zh-group-row__toggle:focus-visible {
    outline: 2px solid var(--electric-cyan);
    outline-offset: 2px;
}

.zh-group-row__marker {
    color: var(--ink-3);
    font-family: var(--font-mono);
}

.zh-group-row__title {
    min-width: 0;
    overflow-wrap: anywhere;
}

/* The count as a system label: mono, the muted ink. */
.zh-group-row__count {
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    letter-spacing: var(--tracking-system);
}

.zh-group-row__rollup,
.zh-group-row__aside {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;
}

.zh-group-row__children {
    display: grid;
    gap: 0.375rem;
    padding-left: 0.75rem;
    border-left: 1px solid var(--hair);
}
</style>
